import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import type { Game, HomeResponse, HomeStats, ProfileId, RelaySession, TodayProgress } from "@/lib/types";
import { GAME_LABEL, GAME_TOTAL } from "@/lib/types";
import { Shell } from "@/components/Shell";
import {
  pairForProfile,
  profilesForProfile,
  PROFILES,
  useActiveProfile,
  useProfileStore,
} from "@/state/profile";
import { api } from "@/lib/api";
import { todayKey } from "@/lib/date";
import { navigate } from "@/lib/router";
import { cn } from "@/lib/cn";

type TodayMap = Record<Game, TodayProgress>;
type HomeMap = Record<ProfileId, HomeStats>;

export function HomePage() {
  const profile = useActiveProfile();
  const activeId = useProfileStore((s) => s.activeId);
  const [home, setHome] = useState<HomeResponse | null>(null);
  const [today, setToday] = useState<TodayMap | null>(null);
  const [relay, setRelay] = useState<RelaySession | null>(null);
  const [queueCount, setQueueCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    Promise.all([
      api.stats.home(),
      api.stats.today(activeId, todayKey()),
      api.study.queue(activeId, todayKey()),
      api.relay.today(activeId, todayKey()),
    ])
      .then(([h, t, q, r]) => {
        if (cancelled) return;
        setHome(h);
        setToday(t);
        setQueueCount(q.cards.length);
        setRelay(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "stats failed");
      });
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  const completedCount = today
    ? (Object.values(today).filter((t) => t.completed).length as number)
    : 0;
  const dayCorrect = today
    ? Object.values(today).reduce((n, t) => n + t.correct, 0)
    : 0;
  const dayTotal = today
    ? Object.values(today).reduce((n, t) => n + t.total, 0)
    : Object.values(GAME_TOTAL).reduce((n, total) => n + total, 0);
  const duo = home?.duos[pairForProfile(activeId)];

  return (
    <Shell>
      <section className="space-y-4">
        <motion.div
          key={profile.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="chunk p-5"
        >
          <div className="flex items-baseline justify-between">
            <h1 className="font-display text-2xl">Misión de hoy</h1>
            <span className="pill">🔥 {duo?.duo_streak ?? 0} juntos</span>
          </div>
          <p className="mt-1 text-sm text-brand-muted font-bold">
            Un relevo corto con tu pareja mantiene vuestra racha.
          </p>
        </motion.div>

        <RelayCard relay={relay} />

        <div className="flex items-baseline justify-between px-1 pt-1">
          <h2 className="font-display text-xl">Práctica extra</h2>
          <span className="text-xs text-brand-muted font-bold">{dayCorrect}/{dayTotal} · {completedCount}/3</span>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {(["el_la", "conj", "vocab"] as const).map((g, i) => (
            <GameCard key={g} game={g} progress={today?.[g] ?? null} delay={0.05 * i} />
          ))}
        </div>

        <StudyCard count={queueCount} />

        <HeadToHead stats={home?.profiles ?? null} activeId={activeId} />
        <AchievementBar stats={home?.profiles ?? null} activeId={activeId} />

        {error && (
          <div className="chunk p-3 text-xs text-brand-rose font-bold">
            error: {error}
          </div>
        )}
      </section>
    </Shell>
  );
}

function RelayCard({ relay }: { relay: RelaySession | null }) {
  const status = relay?.reveal
    ? "Ver resultado"
    : relay?.completed
      ? "Esperando al otro"
      : "Jugar ahora";
  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate({ name: "relay" })}
      className="chunk relative overflow-hidden p-5 text-left bg-gradient-to-br from-brand-coral/25 via-brand-saffron/20 to-brand-violet/20"
    >
      <div className="flex items-start gap-4">
        <span className="text-4xl shrink-0">{relay?.theme_emoji ?? "🤝"}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-brand-muted font-black">Relevo diario · 8 frases</div>
          <div className="font-display text-2xl leading-tight">{relay?.theme_title ?? "Reto en pareja"}</div>
          <div className="mt-1 text-xs text-brand-muted font-bold">Mismo tema · ayuda justa para cada uno</div>
          <span className="pill mt-3 bg-white/70 text-brand-ink">{status} →</span>
        </div>
      </div>
    </motion.button>
  );
}

function GameCard({
  game,
  progress,
  delay,
}: {
  game: Game;
  progress: TodayProgress | null;
  delay: number;
}) {
  const label = GAME_LABEL[game];
  const completed = progress?.completed ?? false;
  const correct = progress?.correct ?? 0;
  const total = progress?.total ?? GAME_TOTAL[game];
  const pct = total ? (correct / total) * 100 : 0;
  const tint = game === "el_la"
    ? "from-brand-saffron/25 to-brand-coral/15"
    : game === "conj"
      ? "from-brand-mint/25 to-brand-sky/15"
      : "from-brand-violet/25 to-brand-rose/15";

  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileTap={{ scale: 0.98 }}
      disabled={completed}
      onClick={() => navigate({ name: "play", game })}
      className={cn(
        "chunk relative overflow-hidden text-left p-5 bg-gradient-to-br",
        tint,
        completed && "opacity-80",
      )}
    >
      <div className="flex items-center gap-3 min-[360px]:gap-4">
        <span className="text-3xl min-[360px]:text-4xl">{label.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl">{label.title}</div>
          <div className="text-sm text-brand-muted font-bold">
            {completed ? `Hecho · ${correct}/${total}` : `${correct}/${total} hoy`}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
            <div
              className="h-full bg-brand-ink/70"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <span
          className={cn(
            "pill px-2 min-[360px]:px-3 shrink-0",
            completed ? "bg-brand-mint/30 border-brand-mint/50 text-brand-ink" : "",
          )}
        >
          {completed ? "✓ Hecho" : "Jugar →"}
        </span>
      </div>
    </motion.button>
  );
}

function StudyCard({ count }: { count: number | null }) {
  const badge =
    count === null ? "…" : count === 0 ? "al día" : `${count} palabra${count === 1 ? "" : "s"}`;
  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate({ name: "study" })}
      className="chunk p-4 text-left bg-gradient-to-br from-brand-sky/15 to-brand-violet/10 relative overflow-hidden"
    >
      <div className="flex items-center gap-3">
        <span className="text-3xl">🗂️</span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg">Ficha de estudio</div>
          <div className="text-xs text-brand-muted font-bold">
            Repasa tus errores con tarjetas
          </div>
        </div>
        <span
          className={cn(
            "pill shrink-0",
            count && count > 0
              ? "bg-brand-saffron/25 border-brand-saffron/50 text-brand-ink"
              : "bg-brand-mint/20 border-brand-mint/40 text-brand-ink",
          )}
        >
          {badge}
        </span>
      </div>
    </motion.button>
  );
}

function HeadToHead({
  stats,
  activeId,
}: {
  stats: HomeMap | null;
  activeId: ProfileId;
}) {
  if (!stats) return null;
  const pair = profilesForProfile(activeId);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="chunk p-4"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-display text-lg">Duelo</div>
        <div className="text-[10px] text-brand-muted uppercase tracking-widest font-black">
          H2H
        </div>
      </div>
      <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-3">
        {pair.map((pid) => {
          const s = stats[pid];
          const active = pid === activeId;
          const p = PROFILES[pid];
          return (
            <div
              key={pid}
              className={cn(
                "rounded-2xl p-3 border-2",
                active ? "border-current" : "border-brand-border",
              )}
              style={{ color: p.color }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="h-8 w-8 rounded-xl grid place-items-center text-white text-sm font-black"
                  style={{ background: p.color }}
                >
                  {p.name[0]}
                </span>
                <div className="text-brand-ink font-display">{p.name}</div>
              </div>
              <div className="mt-2 text-brand-ink">
                <div className="text-2xl font-display leading-none">
                  Nv.{s.level}
                </div>
                <div className="text-[10px] text-brand-muted uppercase tracking-widest font-black">
                  {s.level_title}
                </div>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-center">
                <Mini label="🔥" value={s.day_streak} />
                <Mini label="Acc" value={`${s.accuracy}%`} />
                <Mini label="XP" value={s.xp} />
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[10px]">
                <Mini small label="el/la" value={s.longest_el_la} />
                <Mini small label="conj" value={s.longest_conj} />
                <Mini small label="voc" value={s.longest_vocab} />
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

function Mini({
  label,
  value,
  small,
}: {
  label: string;
  value: string | number;
  small?: boolean;
}) {
  return (
    <div className="rounded-xl bg-brand-canvas px-2 py-1">
      <div
        className={cn(
          "font-display leading-none text-brand-ink",
          small ? "text-sm" : "text-lg",
        )}
      >
        {value}
      </div>
      <div className="text-[9px] uppercase tracking-widest text-brand-muted font-black">
        {label}
      </div>
    </div>
  );
}

function AchievementBar({
  stats,
  activeId,
}: {
  stats: HomeMap | null;
  activeId: ProfileId;
}) {
  if (!stats) return null;
  const s = stats[activeId];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="chunk p-4"
    >
      <div className="flex items-baseline justify-between">
        <div className="font-display text-lg">Progreso</div>
        <div className="text-xs text-brand-muted font-bold">
          {s.xp} XP · {s.xp_to_next > 0 ? `${s.xp_to_next} para subir` : "máximo"}
        </div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-brand-border overflow-hidden">
        <motion.div
          initial={false}
          animate={{
            width: `${Math.min(100, ((s.xp % 500) / 500) * 100)}%`,
          }}
          transition={{ duration: 0.6 }}
          className="h-full bg-brand-violet"
        />
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs font-bold text-brand-muted">
        <span className="text-base">🏅</span>
        <span>
          {s.achievements.length} logro{s.achievements.length === 1 ? "" : "s"} ·{" "}
          {s.lifetime_correct}/{s.lifetime_total} histórico
        </span>
      </div>
      <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
        {s.achievements.length ? s.achievements.map((achievement) => (
          <span key={achievement.code} className="pill whitespace-nowrap bg-brand-saffron/15 border-brand-saffron/40 text-brand-ink normal-case tracking-normal">
            {badgeFor(achievement.code)}
          </span>
        )) : (
          <span className="text-xs text-brand-muted font-bold">Tu primer logro está muy cerca.</span>
        )}
      </div>
    </motion.div>
  );
}

function badgeFor(code: string): string {
  if (code === "first_finish") return "🌱 Primer paso";
  if (code === "first_relay") return "🤝 Primer relevo";
  if (code === "relay_comeback") return "🌈 Segunda oportunidad";
  if (code === "relay_perfect") return "🏆 Relevo perfecto";
  if (code.startsWith("perfect_")) return "✨ Partida perfecta";
  if (code.startsWith("streak10_")) return "🔥 Racha de 10";
  return `🏅 ${code.replaceAll("_", " ")}`;
}
